import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Sparkles, RefreshCw, Loader2, Image as ImageIcon, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface Creative {
  post_date: string;
  theme: string;
  design_name: string;
  image_path: string | null;
  used_fallback: boolean;
  ai_error: string | null;
  updated_at: string;
}

const DailyCreativePreviewCard = () => {
  const { toast } = useToast();
  const [creative, setCreative] = useState<Creative | null>(null);
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("daily-channel-post", {
        body: { type: "get_creative" },
      });
      if (error) throw error;
      const payload = data as any;
      setCreative(payload?.creative ?? null);
      setSignedUrl(payload?.signedUrl ?? null);
    } catch {
      setCreative(null);
      setSignedUrl(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    const ch = supabase
      .channel("daily-creatives-preview")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "daily_creatives" },
        () => load(),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, []);

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke("daily-channel-post", {
        body: { type: "regenerate_creative" },
      });
      if (error) throw error;
      if ((data as any)?.ok) {
        toast({
          title: "✅ New creative generated",
          description: `Theme: "${(data as any).creative?.theme?.slice(0, 60)}..."`,
        });
        await load();
      } else {
        toast({ title: "Regenerate failed", description: "See logs", variant: "destructive" });
      }
    } catch (e: any) {
      toast({ title: "Regenerate failed", description: e.message ?? "Unknown error", variant: "destructive" });
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <Card className="overflow-hidden border-primary/30 bg-gradient-to-br from-primary/5 via-background to-background">
      <CardHeader className="pb-3 border-b">
        <CardTitle className="flex items-center gap-2 text-base">
          <div className="w-7 h-7 rounded-md bg-primary/15 text-primary flex items-center justify-center border border-primary/30">
            <ImageIcon className="w-4 h-4" />
          </div>
          <span className="flex-1">Today's Shared Creative</span>
          <Badge variant="outline" className="text-[10px] gap-1">
            <Sparkles className="w-3 h-3" /> TG · FB · IG
          </Badge>
        </CardTitle>
      </CardHeader>

      <CardContent className="p-4 space-y-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          One creative per day, locked. Telegram, Facebook, and Instagram all
          post this exact image. It will not change until you click
          <span className="font-medium text-foreground"> Change creative</span> below.
        </p>

        {loading ? (
          <Skeleton className="aspect-square w-full rounded-lg" />
        ) : !creative ? (
          <div className="rounded-lg border border-dashed bg-muted/30 p-6 text-center text-xs text-muted-foreground">
            <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
            No creative cached yet for today.
            <div className="mt-1">It will be generated automatically when the next post fires, or click regenerate below.</div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="relative rounded-lg overflow-hidden border bg-muted/20 aspect-square max-w-xs mx-auto">
              {signedUrl ? (
                <img
                  src={signedUrl}
                  alt="Today's daily creative"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">
                  Image unavailable
                </div>
              )}
              {creative.used_fallback && (
                <Badge variant="secondary" className="absolute top-2 right-2 text-[9px] gap-1">
                  <AlertTriangle className="w-3 h-3" /> SVG fallback
                </Badge>
              )}
            </div>

            <div className="text-[11px] space-y-1">
              <div className="flex items-baseline gap-2">
                <span className="text-muted-foreground shrink-0">Date:</span>
                <span className="font-mono">{creative.post_date}</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-muted-foreground shrink-0">Design:</span>
                <span className="font-mono">{creative.design_name}</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-muted-foreground shrink-0">Quote:</span>
                <span className="italic">"{creative.theme}"</span>
              </div>
              {creative.ai_error && (
                <div className="text-destructive text-[10px] mt-1 break-words">
                  AI error: {creative.ai_error}
                </div>
              )}
            </div>
          </div>
        )}

        <Button
          variant="outline"
          size="sm"
          className="w-full"
          disabled={regenerating}
          onClick={regenerate}
        >
          {regenerating ? (
            <>
              <Loader2 className="w-3 h-3 mr-2 animate-spin" />
              Generating new creative (15–45s)…
            </>
          ) : (
            <>
              <RefreshCw className="w-3 h-3 mr-2" />
              Change creative
            </>
          )}
        </Button>

        <p className="text-[10px] text-muted-foreground leading-relaxed border-t pt-2">
          ℹ️ Clicking <span className="font-medium">Change</span> replaces today's
          locked creative with a fresh AI quote + image. Posts already sent
          today won't change, but every future post today (TG · FB · IG) will
          use the new one.
        </p>
      </CardContent>
    </Card>
  );
};

export default DailyCreativePreviewCard;
